import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Cable,
  Wifi,
  Crosshair,
  Thermometer,
  CheckCircle2,
  Sparkles,
  Info,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Helps a B2B visitor pick the right CCTV camera family before drilling into
// product models. Each CTA pre-fills the contact form via the existing
// ?produto=&marca= query prefill hook.

const COPY = {
  pt: {
    overline: "Qual tipo de câmara CCTV escolher?",
    title: "Analógica HD, IP/PoE, PTZ ou Térmica — qual é a certa para si?",
    subtitle:
      "Cada tipo de câmara serve um cenário diferente. Veja qual encaixa no seu projeto e peça orçamento — a TVCabSat instala e dá garantia oficial em Angola.",
    labels: {
      best_for: "Ideal para",
      pros: "Pontos fortes",
      cons: "A considerar",
      tech: "Tecnologia",
      cta: "Pedir orçamento",
      pick: "Mais procurada",
    },
    cta_help: {
      title: "Quer um projeto chave-na-mão?",
      body: "A nossa equipa faz levantamento no local, projeta o sistema (câmaras, NVR/DVR, cablagem, racks) e entrega instalado com garantia.",
      btn: "Pedir levantamento técnico",
    },
  },
  en: {
    overline: "Which CCTV camera type to pick?",
    title: "HD analog, IP/PoE, PTZ or thermal — which is right for you?",
    subtitle:
      "Each camera family fits a different scenario. See which one matches your project and request a quote — TVCabSat installs with official warranty in Angola.",
    labels: {
      best_for: "Best for",
      pros: "Strengths",
      cons: "Trade-offs",
      tech: "Technology",
      cta: "Request a quote",
      pick: "Most popular",
    },
    cta_help: {
      title: "Want a turn-key project?",
      body: "Our team does a site survey, designs the system (cameras, NVR/DVR, cabling, racks) and delivers it installed with warranty.",
      btn: "Book a site survey",
    },
  },
  es: {
    overline: "¿Qué tipo de cámara CCTV elegir?",
    title: "HD analógica, IP/PoE, PTZ o térmica — ¿cuál es la adecuada para ti?",
    subtitle:
      "Cada familia de cámara sirve un escenario distinto. Mira cuál encaja en tu proyecto y solicita presupuesto — TVCabSat instala con garantía oficial en Angola.",
    labels: {
      best_for: "Ideal para",
      pros: "Puntos fuertes",
      cons: "A considerar",
      tech: "Tecnología",
      cta: "Solicitar presupuesto",
      pick: "Más buscada",
    },
    cta_help: {
      title: "¿Quieres un proyecto llave en mano?",
      body: "Nuestro equipo hace levantamiento, diseña el sistema (cámaras, NVR/DVR, cableado, racks) y lo entrega instalado con garantía.",
      btn: "Solicitar levantamiento",
    },
  },
};

const CAMERAS = {
  pt: [
    {
      id: "analog-hd",
      icon: "cable",
      name: "HD Analógica",
      subtitle: "HD-CVI / TVI / AHD",
      featured: false,
      best_for:
        "Lojas, condomínios e retrofit sobre cablagem coaxial já existente.",
      tech: "Sinal sobre cabo coaxial até 500 m, com DVR híbrido. Resoluções 2 MP, 5 MP e 8 MP (4K).",
      pros: [
        "Custo de implementação baixo",
        "Reutiliza cabo coaxial existente",
        "Distâncias longas sem switches",
        "Instalação simples",
      ],
      cons: [
        "Sem analítica avançada (IA limitada)",
        "Necessita DVR no local",
        "Menos flexível em expansão",
      ],
      produto: "Câmaras HD Analógicas (HD-CVI)",
    },
    {
      id: "ip-poe",
      icon: "wifi",
      name: "IP / PoE",
      subtitle: "Rede + Power over Ethernet",
      featured: true,
      best_for:
        "Empresas, indústria, escritórios e instalações que exigem alta resolução e analítica.",
      tech: "Câmaras de rede com PoE (alimentação + dados num cabo Cat. 6). Resoluções de 2 MP a 12 MP, gravação em NVR ou cloud.",
      pros: [
        "Alta resolução (até 12 MP / 4K)",
        "Analítica com IA (deteção, contagem, LPR)",
        "Acesso remoto e multi-site",
        "Escalável e gerível centralmente",
      ],
      cons: [
        "Investimento inicial mais alto",
        "Exige cablagem estruturada Cat. 6",
        "Requer projeto de rede",
      ],
      produto: "Câmaras IP / PoE",
    },
    {
      id: "ptz",
      icon: "crosshair",
      name: "PTZ Motorizada",
      subtitle: "Pan-Tilt-Zoom",
      featured: false,
      best_for:
        "Perímetros amplos, parques, estaleiros, portos e zonas de tráfego que exigem zoom remoto.",
      tech: "Câmaras motorizadas com rotação 360°, inclinação e zoom óptico até 45×. Controlo remoto + auto-tracking.",
      pros: [
        "Cobertura de grandes áreas com 1 câmara",
        "Zoom óptico de longo alcance",
        "Auto-tracking de alvos",
        "Presets e patrulhamento automático",
      ],
      cons: [
        "Custo unitário superior",
        "Cobre 1 ponto de cada vez",
        "Manutenção dos motores",
      ],
      produto: "Câmaras PTZ Motorizadas",
    },
    {
      id: "thermal",
      icon: "thermometer",
      name: "Térmica + IA",
      subtitle: "Visão térmica & deteção",
      featured: false,
      best_for:
        "Perímetros 24/7, instalações críticas, fronteiras e ambientes com escuridão total ou fumo.",
      tech: "Sensores microbolométricos que detetam calor — operam em escuridão absoluta, chuva, fumo. Combinadas com IA para anti-intrusão.",
      pros: [
        "Visão em escuridão total (0 lux)",
        "Funciona em fumo, neblina e chuva",
        "Anti-intrusão por análise de calor",
        "Longo alcance (centenas de metros)",
      ],
      cons: [
        "Não capta detalhe (faces, matrículas)",
        "Custo elevado por unidade",
        "Mais usada como complemento",
      ],
      produto: "Câmaras Térmicas + IA",
    },
  ],
  en: [
    {
      id: "analog-hd",
      icon: "cable",
      name: "HD Analog",
      subtitle: "HD-CVI / TVI / AHD",
      featured: false,
      best_for:
        "Shops, residential and retrofit projects over existing coaxial cabling.",
      tech: "Signal over coax up to 500 m, with a hybrid DVR. Resolutions 2 MP, 5 MP and 8 MP (4K).",
      pros: [
        "Lowest deployment cost",
        "Reuses existing coaxial cable",
        "Long runs without switches",
        "Simple installation",
      ],
      cons: [
        "No advanced AI analytics",
        "Local DVR required",
        "Less flexible to expand",
      ],
      produto: "HD Analog cameras (HD-CVI)",
    },
    {
      id: "ip-poe",
      icon: "wifi",
      name: "IP / PoE",
      subtitle: "Network + Power over Ethernet",
      featured: true,
      best_for:
        "Companies, industry, offices and sites that need high resolution and analytics.",
      tech: "Network cameras over PoE (power + data on Cat. 6). 2 MP–12 MP, recorded to NVR or cloud.",
      pros: [
        "High resolution (up to 12 MP / 4K)",
        "AI analytics (detection, counting, LPR)",
        "Remote / multi-site access",
        "Scalable, centrally managed",
      ],
      cons: [
        "Higher upfront cost",
        "Requires Cat. 6 structured cabling",
        "Needs network design",
      ],
      produto: "IP / PoE cameras",
    },
    {
      id: "ptz",
      icon: "crosshair",
      name: "PTZ Motorized",
      subtitle: "Pan-Tilt-Zoom",
      featured: false,
      best_for:
        "Large perimeters, parks, sites, ports and traffic areas needing remote zoom.",
      tech: "Motorized cameras with 360° rotation, tilt and optical zoom up to 45×. Remote control + auto-tracking.",
      pros: [
        "One camera covers wide areas",
        "Long-range optical zoom",
        "Target auto-tracking",
        "Presets and auto-patrolling",
      ],
      cons: [
        "Higher unit cost",
        "Covers one spot at a time",
        "Motor maintenance",
      ],
      produto: "PTZ Motorized cameras",
    },
    {
      id: "thermal",
      icon: "thermometer",
      name: "Thermal + AI",
      subtitle: "Thermal vision & detection",
      featured: false,
      best_for:
        "24/7 perimeters, critical facilities, borders and zones in total darkness or smoke.",
      tech: "Microbolometer sensors detecting heat — work in absolute darkness, rain, smoke. Combined with AI for intrusion detection.",
      pros: [
        "Vision in absolute darkness (0 lux)",
        "Works in smoke, fog and rain",
        "Heat-based anti-intrusion",
        "Long range (hundreds of meters)",
      ],
      cons: [
        "No detail (faces, plates)",
        "High unit cost",
        "Best as a complement",
      ],
      produto: "Thermal + AI cameras",
    },
  ],
  es: [
    {
      id: "analog-hd",
      icon: "cable",
      name: "HD Analógica",
      subtitle: "HD-CVI / TVI / AHD",
      featured: false,
      best_for:
        "Tiendas, condominios y retrofit sobre cableado coaxial existente.",
      tech: "Señal sobre coaxial hasta 500 m, con DVR híbrido. Resoluciones 2 MP, 5 MP y 8 MP (4K).",
      pros: [
        "Coste de despliegue bajo",
        "Reutiliza el coaxial existente",
        "Distancias largas sin switches",
        "Instalación sencilla",
      ],
      cons: [
        "Sin analítica avanzada (IA limitada)",
        "Requiere DVR local",
        "Menos flexible al expandir",
      ],
      produto: "Cámaras HD Analógicas (HD-CVI)",
    },
    {
      id: "ip-poe",
      icon: "wifi",
      name: "IP / PoE",
      subtitle: "Red + Power over Ethernet",
      featured: true,
      best_for:
        "Empresas, industria, oficinas y sitios que exigen alta resolución y analítica.",
      tech: "Cámaras de red con PoE (alimentación + datos en Cat. 6). De 2 MP a 12 MP, grabación NVR o cloud.",
      pros: [
        "Alta resolución (hasta 12 MP / 4K)",
        "Analítica con IA (detección, conteo, LPR)",
        "Acceso remoto y multi-sitio",
        "Escalable y gestión central",
      ],
      cons: [
        "Mayor inversión inicial",
        "Exige cableado Cat. 6",
        "Requiere proyecto de red",
      ],
      produto: "Cámaras IP / PoE",
    },
    {
      id: "ptz",
      icon: "crosshair",
      name: "PTZ Motorizada",
      subtitle: "Pan-Tilt-Zoom",
      featured: false,
      best_for:
        "Perímetros amplios, parques, obras, puertos y zonas de tráfico con necesidad de zoom remoto.",
      tech: "Cámaras motorizadas con rotación 360°, inclinación y zoom óptico hasta 45×. Control remoto + auto-tracking.",
      pros: [
        "Cobertura de grandes áreas con 1 cámara",
        "Zoom óptico de largo alcance",
        "Auto-tracking de objetivos",
        "Presets y patrullaje automático",
      ],
      cons: [
        "Coste unitario superior",
        "Cubre 1 punto a la vez",
        "Mantenimiento de motores",
      ],
      produto: "Cámaras PTZ Motorizadas",
    },
    {
      id: "thermal",
      icon: "thermometer",
      name: "Térmica + IA",
      subtitle: "Visión térmica & detección",
      featured: false,
      best_for:
        "Perímetros 24/7, instalaciones críticas, fronteras y entornos con oscuridad total o humo.",
      tech: "Sensores microbolométricos que detectan calor — operan en oscuridad absoluta, lluvia, humo. Combinadas con IA antiintrusión.",
      pros: [
        "Visión en oscuridad total (0 lux)",
        "Funciona con humo, niebla y lluvia",
        "Antiintrusión por análisis de calor",
        "Largo alcance (cientos de metros)",
      ],
      cons: [
        "No capta detalle (caras, matrículas)",
        "Coste elevado por unidad",
        "Suele usarse como complemento",
      ],
      produto: "Cámaras Térmicas + IA",
    },
  ],
};

const ICON_MAP = {
  cable: Cable,
  wifi: Wifi,
  crosshair: Crosshair,
  thermometer: Thermometer,
};

const BRAND_LABEL = "CCTV & Vigilância";

export const CctvCompare = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const copy = COPY[lang] || COPY.pt;
  const cameras = CAMERAS[lang] || CAMERAS.pt;

  const requestQuote = (produto) => {
    const params = new URLSearchParams({ produto, marca: BRAND_LABEL });
    navigate(`/?${params.toString()}#contact`);
  };

  return (
    <section
      id="cctv-compare"
      data-testid="cctv-compare"
      className="relative scroll-mt-24 overflow-hidden border-t border-slate-200 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-20 text-white lg:py-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-1/4 h-[500px] w-[500px] rounded-full bg-blue-500/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/4 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-cyan-300">
            {copy.overline}
          </div>
          <h2
            data-testid="cctv-compare-title"
            className="font-heading mt-4 text-3xl font-black tracking-tight text-white md:text-4xl lg:text-5xl"
          >
            {copy.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">
            {copy.subtitle}
          </p>
        </div>

        {/* 4-card grid */}
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {cameras.map((c) => {
            const Icon = ICON_MAP[c.icon] || Wifi;
            return (
              <article
                key={c.id}
                data-testid={`cctv-compare-${c.id}`}
                className={`group relative flex flex-col rounded-3xl border bg-slate-900/60 p-6 shadow-xl backdrop-blur-sm transition-all hover:-translate-y-1 hover:shadow-2xl ${
                  c.featured
                    ? "border-cyan-400/50 ring-2 ring-cyan-400/40"
                    : "border-white/10"
                }`}
              >
                {c.featured && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-cyan-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-900 shadow-lg">
                    <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                    {copy.labels.pick}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div
                    className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                      c.featured
                        ? "bg-cyan-500/20 text-cyan-300"
                        : "bg-blue-500/15 text-blue-300"
                    }`}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-bold tracking-tight text-white">
                      {c.name}
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
                      {c.subtitle}
                    </div>
                  </div>
                </div>

                {/* Best for */}
                <div className="mt-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">
                    {copy.labels.best_for}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-200">
                    {c.best_for}
                  </p>
                </div>

                {/* Tech */}
                <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="flex items-center gap-1.5">
                    <Info
                      className="h-3 w-3 text-cyan-300"
                      strokeWidth={2.5}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">
                      {copy.labels.tech}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[12.5px] leading-snug text-slate-300">
                    {c.tech}
                  </p>
                </div>

                {/* Pros */}
                <div className="mt-4 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">
                    {copy.labels.pros}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {c.pros.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-1.5 text-[12.5px] leading-snug text-slate-200"
                      >
                        <CheckCircle2
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400"
                          strokeWidth={2.5}
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cons */}
                <div className="mt-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
                    {copy.labels.cons}
                  </div>
                  <ul className="mt-2 space-y-1">
                    {c.cons.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-1.5 text-[12px] leading-snug text-slate-400"
                      >
                        <span
                          aria-hidden
                          className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-amber-400"
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => requestQuote(c.produto)}
                  data-testid={`cctv-compare-cta-${c.id}`}
                  className={`mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                    c.featured
                      ? "bg-cyan-400 text-slate-900 hover:bg-cyan-300"
                      : "bg-white text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  {copy.labels.cta}
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </article>
            );
          })}
        </div>

        {/* Help CTA */}
        <div className="mt-12 rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-slate-900 via-cyan-950/30 to-slate-900 p-6 md:p-8">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h3 className="font-heading text-lg font-bold tracking-tight text-white md:text-xl">
                {copy.cta_help.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-300">
                {copy.cta_help.body}
              </p>
            </div>
            <a
              href="/#contact"
              data-testid="cctv-compare-help-cta"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-900 shadow-md transition-all hover:-translate-y-0.5 hover:bg-cyan-300 hover:shadow-lg"
            >
              {copy.cta_help.btn}
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CctvCompare;
